import api from '../api.js';
import db from '../../versa/db.js';
import crud from '../../versa/crud.js';
import filter, { resolveFilterLabelsToIds } from '../../versa/filter.js';
import router from '../../versa/router.js';
import formset from '../../versa/formset.js';
import { flatten, deepen } from '../../versa/record.js';
import { notify, ask } from '../../versa/msgbox.js';
import { splitIdsByCompany } from '../../versa/misc.js';
import pdf from '../../versa/pdf.js';
import view from '../views/output-orders.js';
import form from '../forms/output-orders.js';
import output_stats_view from '../views/output-orders-output-stats.js';
import create_outputs_form from '../forms/output-orders-create-outputs.js';
import upload_form from '../forms/output-orders-upload.js';
import order_upload_result_view from '../views/order-upload-result.js';
import * as types from '../types.js';

const outputOrders = function()
{

	let output_orders_crud = crud.create({
		async loadBaseData()
		{
			api.call(api.list_companies, this.queryAll()).then((data) => db.set('companies', data).catch(error => notify(error)));
			api.call(api.list_programs, this.queryAll()).then((data) => db.set('programs', data).catch(() => {}));
			api.call(api.list_warehouses, this.queryAll()).then((data) => db.set('warehouses', data).catch(error => notify(error)));
			api.call(api.list_units, this.queryAll()).then((data) => db.set('units', data).catch(error => notify(error)));
			api.call(api.list_products, this.queryAll()).then((data) => db.set('products', data).catch(error => notify(error)));
			api.call(api.list_lots, this.queryAll()).then((data) => db.set('lots', data).catch(error => notify(error)));
			api.call(api.list_price_lists, this.queryAll()).then((data) => db.set('price_lists', data).catch(error => notify(error)));
			api.call(api.list_payment_terms, this.queryAll()).then((data) => db.set('payment_terms', data).catch(error => notify(error)));
			api.call(api.list_customers, this.queryAll()).then((data) => db.set('customers', data).catch(error => notify(error)));
			api.call(api.list_carriers, this.queryAll()).then((data) => db.set('carriers', data).catch(error => notify(error)));
			api.call(api.list_users, this.queryAll()).then((data) => db.set('users', data).catch(error => notify(error)));
		},
		async queryData(query)
		{
			return await api.call(api.list_output_orders, query);
		},
		async loadRecord(id)
		{
			let _record = await api.call(api.select_output_order, ...JSON.parse(id));
			return flatten(_record, 'items');
		},
		async saveRecord(data, old_data)
		{
			data = deepen(data, 'items');
			let _record = {
				reference: data.reference,
				customer_id: Number(data.customer_id),
				carrier_id: Number(data.carrier_id),
				program_id: data.program_id != null && data.program_id !== '' ? Number(data.program_id) : null,
				service_channel: data.service_channel,
				payment_method: data.payment_method,
				payment_term_id: (data.payment_method === '90' || data.payment_method === 90) && (data.payment_term_id == null || data.payment_term_id === '')
					? null
					: Number(data.payment_term_id),
				freight_type: data.freight_type,
				freight_value: Number(data.freight_value),
				discount: (data.discount != null && data.discount !== '') ? Number(data.discount) : null,
				notes: data.notes?.trim(),
				is_return: data.is_return == '1',	// Can be: undefined, '0', '1'
				status: Number(data.status),
				items: (data.items || [])
					.filter(item => item.product_id != null && item.product_id !== '' && !isNaN(Number(item.product_id)))
					.map(item => ({
						product_id: Number(item.product_id),
						lot_id: (item.lot_id != null && item.lot_id !== '' && !isNaN(Number(item.lot_id)) && Number(item.lot_id) >= 0) ? Number(item.lot_id) : null,
						unit_id: Number(item.unit_id),
						quantity: Number(item.quantity),
						discount: (item.discount != null && item.discount !== '') ? Number(item.discount) : null,
						unit_price: Number(item.unit_price)
					}))
			};
			let resp;
			if (old_data)
				resp = await api.call(api.update_output_order, data.company_id, data.id, _record);
			else
				resp = await api.call(api.insert_output_order, data.company_id, _record);
			if (resp?.duplicate_reference) {
				const { id: orderId, company_nick } = resp.duplicate_reference;
				const tenant = company_nick ? ` (empresa ${company_nick})` : '';
				notify(`Já existe o pedido de saída nº ${orderId}${tenant} com essa referência. Verifique se não é duplicado.`, 'Atenção');
			}
			return resp;
		},
		editFilter() {
			filter.edit(this.applyFilter.bind(this), this.filter);
		},
		async applyFilter(data) {
			this.filter = await resolveFilterLabelsToIds(data, this.fieldspecs);
			this.loadPage(1);
		},
		createOutputs()
		{
			const showResult = async (results) =>
			{
				if (results == null || results === undefined)
					return;	// Erro já tratado pelo http/notify
				notify(await output_stats_view(results), 'Resultados');
				this.loadPage();	// Show updated order status
				fs.destroy();
			}
			const sendRequest = async (data) =>
			{
				let params = {
					'warehouse_company_id': data.company_id,
					'warehouse_id': Number(data.warehouse_id),
					'reserve_flag': Boolean(data?.reserve_flag),
					'include_expired': Boolean(data?.include_expired),
					'ids': await this.getSelectedIds(['company_id', 'id'])
				};
				let resp = await api.call(api.create_outputs, params);
				if (resp != null)
					await showResult(resp);
			}
			let fs = formset.create(create_outputs_form, sendRequest);
			fs.init = (() =>
			{
				document.querySelector(`${fs.form.selector} #count`).innerHTML = this.getCount();
				Object.getPrototypeOf(fs).init.call(fs);
			})();
		},
		uploadFromSpreadsheet()
		{
			const showResult = (result) =>
			{
				notify(order_upload_result_view(result, 'output'), 'Resultado do upload');
				this.loadPage();
				fs.destroy();
			};
			const sendRequest = async (data) =>
			{
				const formNode = document.querySelector(upload_form.form.selector);
				const fileInput = formNode?.querySelector('#file');
				const file = fileInput?.files?.[0];
				if (!file)
				{
					notify('Selecione um arquivo CSV ou XLSX.');
					return;
				}
				const company_id = data.company_id;
				if (!company_id)
				{
					notify('Selecione a empresa.');
					return;
				}
				const fd = new FormData();
				fd.append('file', file);
				fd.append('customer_id', data.customer_id);
				fd.append('program_id', data.program_id || '');
				const result = await api.call(api.upload_output_order, company_id, fd);
				if (result)
					showResult(result);
			};
			const setupTemplateLink = (fs) =>
			{
				const link = fs.dialog.getNode().querySelector('#download-template');
				if (link)
				{
					link.onclick = async (e) =>
					{
						e.preventDefault();
						const company_id = fs.form.getValues().company_id;
						if (!company_id)
						{
							notify('Selecione a empresa para baixar o modelo.');
							return;
						}
						const csv = await api.call(api.order_upload_template, company_id);
						if (csv && typeof csv === 'string')
						{
							const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
							const a = document.createElement('a');
							a.href = URL.createObjectURL(blob);
							a.download = 'modelo_pedido_upload.csv';
							a.click();
							URL.revokeObjectURL(a.href);
						}
					};
				}
			};
			const fs = formset.create(upload_form, sendRequest);
			fs.init = (() =>
			{
				Object.getPrototypeOf(fs).init.call(fs);
				setupTemplateLink(fs);
			})();
		},
		printPendingOrders()
		{
			const showResult = async (resp) =>
			{
				this.clearGridSelection();
				if (resp?.pdf)
					pdf(resp.pdf);
			}
			const sendRequest = async () =>
			{
				let ids = splitIdsByCompany(await this.getSelectedIds(['company_id', 'id']));
				let resp = await api.call(api.print_pending_output_orders, { 'ids': ids }).then(showResult);
			}
			ask(`Incluir ${this.getCount()} saídas no relatório?`, 'Pick List', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
	});
	
	// Index = position in the backend's SELECT
	let fieldspecs = [
		{ index: 1, field: 'company_id', label: 'Empresa', types: [types.datalist()], placeholder: '', title: 'Empresa', store: 'companies', labels: ['nick'] },
		{ index: 2, field: 'id', label: 'Pedido', types: [types.number(0, 2)], placeholder: '0', title: 'Identificador do pedido' },
		{ index: 3, field: 'reference', label: 'Referência', types: [], placeholder: '', title: 'Código de referência externa do pedido' },
		{ index: 4, field: 'created_on', label: 'Data Criação', types: [types.datetime()], placeholder: 'dd/mm/aaaa hh:mm:ss', title: 'Data e hora de criação do pedido' },
		{ index: 5, field: 'created_by', label: 'Criado Por', types: [types.datalist()], placeholder: '', title: 'Autor da criação do pedido', store: 'users', labels: ['name'] },
		{ index: 6, field: 'updated_on', label: 'Data Alteração', types: [types.datetime()], placeholder: 'dd/mm/aaaa hh:mm:ss', title: 'Data e hora da última atualização do pedido' },
		{ index: 7, field: 'updated_by', label: 'Alterado Por', types: [types.datalist()], placeholder: '', title: 'Autor da última atualização do pedido', store: 'users', labels: ['name'] },
		{ index: 8, field: 'customer_id', label: 'Cliente', types: [types.datalist()], placeholder: '', title: 'Cliente', store: 'customers', labels: ['nick'] },
		{ index: 9, field: 'carrier_id', label: 'Transportador', types: [types.datalist()], placeholder: '', title: 'Transportador', store: 'carriers', labels: ['nick'] },
		{ index: 10, field: 'program_id', label: 'Programa', types: [types.datalist()], placeholder: '', title: 'Programa (estrutura de estoque)', store: 'programs', labels: ['name'] },
		{ index: 11, field: 'service_channel', label: 'Atendimento',	types: [types.opt_service_channel(), types.datalist()], placeholder: '', title: 'Canal de atendimento' },
		{ index: 12, field: 'payment_method', label: 'Pagamento', types: [types.opt_payment_method(), types.datalist()], placeholder: '', title: 'Forma de pagamento' },
		{ index: 13, field: 'payment_term_id', label: 'Condição', types: [types.datalist()], placeholder: '', title: 'Condição de pagamento', store: 'payment_terms', labels: ['name'] },
		{ index: 14, field: 'freight_type', label: 'Tipo Frete', types: [types.opt_freight_type(), types.datalist()], placeholder: '', title: 'Tipo de frete' },
		{ index: 15, field: 'freight_value', label: 'Valor Frete', types: [types.number(2)], placeholder: '0,00', title: 'Valor do frete' },
		{ index: 16, field: 'discount', label: '% Desconto', types: [types.number(2)], placeholder: '0,00', title: 'Percentual de desconto' },
		{ index: 17, field: 'value', label: 'Valor', types: [types.number(2)], placeholder: '0,00', title: 'Valor do pedido' },
		{ index: 18, field: 'notes', label: 'Observações', types: [], placeholder: '', title: 'Observações do pedido' },
		{ index: 19, field: 'is_return', label: 'Devolução', types: [types.opt_bool(), types.datalist()], placeholder: '', title: 'Indicador de devolução' },
		{ index: 20, field: 'status', label: 'Status', types: [types.opt_output_order_status(), types.datalist()], placeholder: '', title: 'Status do pedido' },
	];

	let operations = [
		{ hash: '#create-outputs', label: 'Gerar&nbsp;saídas' },
		{ hash: '#upload', label: 'Upload&nbsp;planilha' },
	];

	let reports = [
		{ hash: '#print-pending', 	label: 'Relatório&nbsp;de&nbsp;Pendências'	},
	];

	router.set('#create-outputs', output_orders_crud.createOutputs.bind(output_orders_crud));

	router.set('#upload', output_orders_crud.uploadFromSpreadsheet.bind(output_orders_crud));

	router.set('#print-pending', output_orders_crud.printPendingOrders.bind(output_orders_crud));

	output_orders_crud.run(
		'output_orders',
		'Pedidos&nbsp;Saída',
		document.getElementById('workarea'),
		view,
		form,
		fieldspecs,
		operations,
		reports
	);

};

export default outputOrders;

